import {
  Alignment,
  arrow,
  autoUpdate,
  flip,
  hide,
  Middleware,
  offset,
  Padding,
  Placement,
  safePolygon,
  shift,
  Side,
  Strategy,
  useClick,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classnames from 'classnames';
import React, {
  cloneElement,
  CSSProperties,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Portal } from '~/components/portal';

import styles from './Tooltip.module.scss';

export enum TooltipTrigger {
  Click = 'click',
  Focus = 'focus',
  Hover = 'hover',
}

export interface TooltipProps {
  ref?: Ref<HTMLElement>;
  label: React.ReactNode;
  placement?: Placement;
  children: ReactElement<Record<string, unknown>>;
  isOpen?: boolean;
  disabled?: boolean;
  inPortal?: boolean;
  dismissible?: boolean;
  hasDelay?: boolean;
  strategy?: Strategy;
  offsetX?: number;
  offsetY?: number;
  onOpen?: () => void;
  onClose?: () => void;
  hasArrow?: boolean;
  trigger?: TooltipTrigger | TooltipTrigger[];
  closeLabel?: string;
  isTouchDevice?: boolean;
  className?: string;
  hidePadding?: Padding;
  withSafePolygon?: boolean;
  hasMaxWidth?: boolean;
  shiftOptions?: Parameters<typeof shift>[0];
}

interface ArrowStyleParams {
  x?: number;
  y?: number;
  placement: Side;
  alignment?: Alignment;
}

const getArrowStyle = ({
  x,
  y,
  placement,
  alignment,
}: ArrowStyleParams): CSSProperties => {
  const getPositionValue = (
    side: 'top' | 'right' | 'bottom' | 'left',
    n?: number
  ): CSSProperties => {
    if (alignment === 'start') {
      return { [side]: 8 };
    }

    if (alignment === 'end') {
      const oppositeSide: Record<string, string> = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      };

      return { [oppositeSide[side]]: 8 };
    }

    return { [side]: n };
  };

  switch (placement) {
    case 'left':
      return { left: -4, ...getPositionValue('top', y) };
    case 'right':
      return { right: -4, ...getPositionValue('top', y) };
    case 'bottom':
      return { bottom: -4, ...getPositionValue('left', x) };
    case 'top':
    default:
      return { top: -4, ...getPositionValue('left', x) };
  }
};

export const Tooltip = ({
  ref,
  isOpen,
  children,
  label,
  placement = 'top',
  disabled = false,
  inPortal = false,
  strategy = 'absolute',
  dismissible = true,
  hasDelay = true,
  offsetX = 0,
  offsetY = 0,
  onOpen,
  onClose,
  hasArrow = true,
  withSafePolygon,
  trigger = [TooltipTrigger.Click, TooltipTrigger.Focus, TooltipTrigger.Hover],
  closeLabel,
  isTouchDevice = false,
  className,
  hidePadding = 0,
  hasMaxWidth = true,
  shiftOptions,
}: TooltipProps) => {
  const arrowRef = useRef(null);
  const [open, setOpen] = useState(isOpen);

  const handlers = Array.isArray(trigger) ? trigger : [trigger];

  const setOpenTooltip = useCallback(
    (nextValue: boolean) => {
      if (!disabled) {
        setOpen(nextValue);

        if (nextValue) {
          onOpen?.();
        } else {
          onClose?.();
        }
      } else {
        setOpen(false);
      }
    },
    [disabled, onOpen]
  );

  const { x, y, middlewareData, context, refs } = useFloating({
    placement,
    open,
    onOpenChange: setOpenTooltip,
    strategy,
    middleware: [
      offset(5),
      flip(),
      shift({ ...shiftOptions, padding: 5 }),
      hasArrow ? arrow({ element: arrowRef }) : undefined,
      hide({
        padding: hidePadding,
      }),
    ].filter((m): m is Middleware => Boolean(m)),
    whileElementsMounted: autoUpdate,
  });

  const optionalUseHoverParams = withSafePolygon
    ? { handleClose: safePolygon() }
    : {};

  const { delay } = useDelayGroup(context, { enabled: hasDelay });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: handlers.includes(TooltipTrigger.Hover),
      delay,
      ...optionalUseHoverParams,
    }),
    useFocus(context, {
      enabled: handlers.includes(TooltipTrigger.Focus),
    }),
    useClick(context, {
      enabled: handlers.includes(TooltipTrigger.Click),
      ignoreMouse: handlers.includes(TooltipTrigger.Hover),
    }),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context, { enabled: dismissible }),
  ]);

  const refCallback = (node: HTMLElement | null) => {
    refs.setReference(node);

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const coords = middlewareData.arrow;
  const referenceHidden = middlewareData?.hide?.referenceHidden;

  const arrowPosition = context.placement || placement;

  const oppositeSides: Record<string, Side> = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  const [sidePart, alignPart] = arrowPosition.split('-');
  const staticSide = oppositeSides[sidePart] ?? 'bottom';

  const alignment: Alignment | undefined =
    alignPart === 'start' || alignPart === 'end' ? alignPart : undefined;

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    return () => setOpen(false);
  }, []);

  const tooltip = open && (
    <div
      ref={refs.setFloating}
      onClick={() => isTouchDevice && setOpen(false)}
      className={classnames(
        styles.tooltipBubble,
        !hasMaxWidth && styles.tooltipBubbleNoMaxWidth,
        className
      )}
      {...getFloatingProps({
        style: {
          visibility: referenceHidden ? 'hidden' : 'visible',
          position: strategy,
          top: (y ?? 0) + offsetY,
          left: (x ?? 0) + offsetX,
          zIndex: 'var(--z-tooltip)',
        },
      })}
      id="tooltip-content"
      data-testid="tooltip-content"
      data-tooltip-bubble=""
    >
      {label}
      {isTouchDevice && (
        <span
          className={styles.mobileTapClose}
          data-testid="close-label"
          data-tooltip-close=""
        >
          {closeLabel}
        </span>
      )}
      {hasArrow && (
        <div
          className={styles.arrowIcon}
          data-testid="tooltip-arrow"
          data-tooltip-arrow=""
          ref={arrowRef}
          style={getArrowStyle({
            x: coords?.x,
            y: coords?.y,
            placement: staticSide,
            alignment,
          })}
        />
      )}
    </div>
  );

  return (
    <>
      {cloneElement(children, {
        ref: refCallback,
        ...getReferenceProps(children.props),
      })}
      {inPortal ? <Portal>{tooltip}</Portal> : tooltip}
    </>
  );
};
